import React, {useEffect, useState} from 'react';
import DashboardContainer from '../sideBar/DashboardContainer';
import {useNavigate} from 'react-router-dom';
import {newTestCheckPakAction, newTestGetStoredSMILESAction} from '../../redux/toolkit/newTestSlice';
import {availableAssaysAction} from '../../redux/toolkit/availableAssaysSlice';
import {useDispatch, useSelector} from 'react-redux';
import { logoutAction } from '../../redux/toolkit/loginSlice';
import PageLayout, {Card, CardHeader, CardBody, PREDICT_STEPS} from '../layout/PageLayout';
import c from './newTest.module.css';
import Modal from './Modal';

const NewTestContainer = () => {
    let navigate = useNavigate();
    const dispatch = useDispatch();
    const {statusCodeGetPak, checkPakResult, smilesResult} = useSelector(state => state?.newTestCheckPakReducer);

    const plReqIdLS = localStorage.getItem('platform_request_id');
    
    const [message, setMessage] = useState('');
    const [myArraySmiles, setMyArraySmiles] = useState([]);
    const [platformRequestId, setPlatformRequestId] = useState(plReqIdLS);
    const [textareaValue, setTextareaValue] = useState('');
    const [showModal, setShowModal] = useState(false);
    // Off by default, deliberately: each comment costs a model call plus web
    // searches, and it is only ever generated for the FIRST compound.
    const [aiComment, setAiComment] = useState(false);

    // TWO gates, read separately. The box is disabled either way, but the reason
    // beside it has to differ: "not enabled for this account" is something the
    // customer acts on by asking us, and "not available during trial" is something
    // they act on by upgrading. One combined flag could not tell them apart.
    //
    // `is_trial` is true only when the account holds NO active PAID subscription.
    // A paying customer can also hold free per-endpoint trials, so the flag is
    // computed from the paid side in the backend rather than from the presence of
    // a free subscription.
    const assays = useSelector(
        state => state?.availableAssaysReducer?.availableAssaysResult);
    const isTrial = assays?.is_trial === true;
    // Absent reads as NOT granted. A front end newer than the backend would see
    // undefined here, and for a feature that bills per use the safe way to be wrong
    // is off - the server refuses it regardless.
    const aiAllowed = assays?.ai_comment_allowed === true;
    const aiBlocked = !aiAllowed || isTrial;
    // Trial takes precedence when both apply: it is the gate the customer can clear
    // without us, and telling an ungranted trial user to ask for access would send
    // them round a loop that ends back here.
    //
    // The label says "AI" although the PAGE title does not, and that difference is
    // deliberate. The page names a section of a formal report, where "AI-generated
    // comment" read as a remark rather than part of the document. The checkbox is
    // where the customer CHOOSES, and the page itself says it is machine-written and
    // unreviewed - so they have to know that before they tick it, not after they
    // have read it. "Interpretation" is the word that links the two: they get the
    // thing they ticked, under the name they ticked it by.
    const aiReason = isTrial
        ? 'Not available during trial.'
        : (!aiAllowed
            ? 'Not enabled for this account - contact support@toxometris.ai.'
            : 'One page, for the first compound only. Machine-written - check the sources.');
  
    const { status_code } = checkPakResult;

    const compoundCount = textareaValue.split('\n').filter(l => l.trim()).length;

    useEffect(() => {
        // Fetched here as well as on the Endpoints step, because the AI-comment
        // checkbox below has to know whether this account is on trial - and this
        // is the first step, so nothing has asked yet. Without it a trial user
        // sees an enabled box that the submit step then silently ignores.
        dispatch(availableAssaysAction({'service_type': 'subscribed'}));

        // A choice from an abandoned run must not carry into this one. The box is
        // off by default and this is where a run begins, so the stored answer is
        // cleared here and written again only when Next is pressed.
        sessionStorage.removeItem('ai_comment');
    }, [dispatch]);

    useEffect(() => {
      // A New Test opened without an eLab platform_request is a direct/manual
      // session — clear any stale eLab marker so the "Upload reports to eLabNext"
      // checkbox on the result page won't show for non-eLab predictions.
      if (!localStorage.getItem('platform_request_id')) {
        localStorage.removeItem('platform_id');
      }
    }, []);

    useEffect(() => {
      if (status_code === 401) {
        dispatch(logoutAction());
        navigate("/");
        window.location.reload();
      }
    }, [dispatch, status_code, navigate]);

    useEffect(()=>{
        if(checkPakResult?.status_code === 401){
            localStorage.removeItem('access_token_toxometris');
            window.location.href = '/';
        }
    },[checkPakResult?.status_code])

    useEffect(() => {
        if (platformRequestId) {
            dispatch(newTestCheckPakAction({
                'platform_request_id': platformRequestId
            }));
        }
    }, [dispatch, platformRequestId]);

    useEffect(() => {
        if (checkPakResult?.status_code === 200 && platformRequestId && checkPakResult.response) {
            window.location.href = checkPakResult.response;
        } else if (platformRequestId && checkPakResult?.status_code === 200 && !checkPakResult.response) {
            dispatch(newTestGetStoredSMILESAction({
                'platform_request_id': platformRequestId
            }))
        }
    }, [checkPakResult.response, checkPakResult?.status_code, platformRequestId, dispatch])

    useEffect(() => {
        if (platformRequestId && statusCodeGetPak === 200) {
            dispatch(newTestGetStoredSMILESAction({
                'platform_request_id': platformRequestId
            }))
        }
    }, [dispatch, platformRequestId, statusCodeGetPak])

    useEffect(() => {
        if (smilesResult.compounds?.length > 0) {
            setMyArraySmiles(smilesResult.compounds)
            setTextareaValue(smilesResult.compounds?.join('\n'));
        } else {
            setMyArraySmiles([])
            setTextareaValue('');
        }
    }, [smilesResult]);

    // Show modal when the specific API error occurs
    useEffect(() => {
        
        if (smilesResult?.status === 'error' && 
            smilesResult?.status_code === 400 && 
            smilesResult?.message === 'elab_api_null_response') {
            setShowModal(true);
        }
    }, [smilesResult]);

    const handleTextareaChange = (event) => {
        if (message) {
            setMessage('');
        }
        if (platformRequestId) {
            setPlatformRequestId(null);
        }
        setTextareaValue(event.target.value);
        setMyArraySmiles(event.target.value.split('\n'));
    };

    const handleAiCommentChange = () => {
        setAiComment(!aiComment);
    };

    const handleSubmit = () => {
        setPlatformRequestId(null);
        if (myArraySmiles?.length > 0 && !message && textareaValue) {
            // sessionStorage rather than router state: the wizard hands step 2 a
            // bare array of SMILES, and step 3 reads nothing from the router at
            // all - it rebuilds the request from Redux. Threading a second value
            // through both hops would change two components that have no interest
            // in this flag. sessionStorage also dies with the tab, so a forgotten
            // tick cannot turn a paid feature on days later.
            sessionStorage.setItem('ai_comment', aiComment && !aiBlocked ? '1' : '');
            navigate('/checkValidate', {state: myArraySmiles});
        } else setMessage('Please fill the list');
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    return (
        <DashboardContainer>
            <PageLayout title='New Test'
                        subtitle='Paste one SMILES or InChI per line. They are validated before any prediction runs.'
                        steps={PREDICT_STEPS} currentStep={0} narrow>
                <Card padded={false}>
                    <CardHeader title='Compounds'
                                actions={<span className={c.compoundCount}>{compoundCount} compound{compoundCount === 1 ? '' : 's'}</span>}/>
                    <CardBody>
                        <textarea placeholder={'CCO\nc1ccccc1\nCC(=O)Oc1ccccc1C(=O)O'}
                                  value={textareaValue}
                                  onChange={handleTextareaChange}
                                  rows='10' className={c.newTestTextarea}
                        />
                        {message && <p className={c.newTestError}>{message}</p>}
                        <div className={c.aiComment}>
                            <input id='aiComment' type='checkbox'
                                   checked={aiComment && !aiBlocked}
                                   disabled={aiBlocked}
                                   onChange={handleAiCommentChange}/>
                            <label htmlFor='aiComment'
                                   className={aiBlocked ? c.aiCommentDisabled : undefined}>
                                Add AI interpretation of the results
                            </label>
                            {/* Says "first compound" out loud: the box sits under a
                                textarea that may hold hundreds, and the cost only
                                makes sense if the reader knows it is one. When it is
                                greyed out, say WHICH reason - a disabled control with
                                no reason reads as a bug, and a disabled control with
                                the wrong reason sends the customer somewhere useless. */}
                            <span className={c.aiCommentHint}>{aiReason}</span>
                        </div>
                        <div className={c.newTestButton}>
                            <button onClick={handleSubmit}>Next</button>
                        </div>
                    </CardBody>
                </Card>
            </PageLayout>

            <Modal isOpen={showModal} onClose={handleCloseModal}>
                <h4>Missing Chemical Structure Information</h4>
                <div className={c.modalTextContent}>
                    <p>The sample(s) you have selected do not contain information about their chemical structure. Each sample must have a defined chemical formula in one of the following formats — SMILES or InChI — in order to enable prediction.</p>
                    <br/>
                    <p>If you want to make a test prediction, please add an arbitrary SMILES formula in textbox such as <b>C1=CC=CC=C1</b> and click Next.</p>
                    <br/><br/>
                    <p style={{fontSize: '12px'}}>Please note that SMILES and InChI fields are not enabled by default in the SciSure / eLabNext environment. To enable them, follow these steps:</p>
                    
                    <ol style={{fontSize: '12px'}}>
                        <li style={{fontSize: '12px'}}>Go to Configuration → Sample Types.</li>
                        <li style={{fontSize: '12px'}}>Click the Add Sample Type button and select Chemical as the sample type.</li>
                        <li style={{fontSize: '12px'}}>After adding the chemical type, click the Edit button, then select Add New Field.</li>
                        <li style={{fontSize: '12px'}}>In the Field Name textbox, enter SMILES or InChI, and click Save.</li>
                    </ol>
                    
                    <p style={{fontSize: '12px'}}>Once completed, you will be able to add chemical structure information to your compounds. For more information please contact <a href="mailto:support@toxometris.ai">support@toxometris.ai</a></p>
                </div>
            </Modal>
        </DashboardContainer>
    );
};

export default NewTestContainer;